import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  ListChecks,
  Search,
  Filter,
  FileText,
  TrendingUp,
  Shield,
  CheckCircle,
  Zap,
  Globe,
  Lock,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import LanguageSelector from "@/components/LanguageSelector";

export default function AnalisiRisultatiAi() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Analisi Risultati AI"
        description="Analisi AI dei risultati bandi UE: sintesi, prioritizzazione e insight azionabili. Estrai informazioni chiave dai bandi TED più velocemente con TED Monitor."
        path="/it/analisi-risultati-ai"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/analisi-risultati-ai" },
          { lang: "en", href: "https://tedmonitor.eu/ai-results-analysis" },
          { lang: "x-default", href: "https://tedmonitor.eu/ai-results-analysis" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        {/* Hero */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-primary/10 text-primary text-xs font-medium">
              <Sparkles className="h-3.5 w-3.5" />
              Insight sui bandi con AI
            </div>

            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Analisi AI dei risultati bandi per decisioni più rapide
            </h1>

            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Quando si monitora il procurement pubblico UE, il collo di bottiglia non è trovare i bandi—è leggere,
              interpretare e prioritizzare i risultati su larga scala. L'analisi AI dei risultati di TED Monitor ti
              aiuta a estrarre le informazioni essenziali, sintetizzare gli avvisi e concentrarti sulle opportunità che
              corrispondono ai tuoi criteri.
            </p>

            {/* Internal links (SEO cluster) */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio bandi UE
              </Link>
              <Link
                to="/it/alert-bandi-ted"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Alert bandi
              </Link>
              <Link
                to="/it/software-appalti-pubblici-europei"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Software appalti
              </Link>
              <Link
                to="/it/accesso-multilingue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Accesso multilingue
              </Link>
              <Link
                to="/it/conformita-sicurezza"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Conformità e sicurezza
              </Link>
              <Link
                to="/it/prezzi"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Prezzi
              </Link>
            </div>

            <p className="mt-6 text-xs text-muted-foreground">
              Fonte ufficiale:{" "}
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                TED (Tenders Electronic Daily)
              </a>{" "}
              – Supplemento alla Gazzetta Ufficiale dell'Unione Europea.
            </p>
          </div>
        </section>

        {/* Cosa fa */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <ListChecks className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cosa fa l'analisi AI dei risultati</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il layer AI supporta il workflow post-ricerca: ti aiuta a comprendere i risultati più velocemente e
              identificare i bandi più rilevanti senza dover esaminare manualmente ogni avviso.
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Sintetizza gli avvisi di gara in highlight chiari e azionabili",
                "Estrae elementi chiave (ambito, committente, scadenze, località, indicazioni procedurali, requisiti)",
                "Evidenzia segnali di corrispondenza vs. mancata corrispondenza con il tuo intento di monitoraggio",
                "Supporta la prioritizzazione quando hai molti risultati",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              Questo è progettato per ridurre i tempi di lettura e accelerare le decisioni "partecipo / non partecipo"
              per le gare.
            </p>
          </div>
        </section>

        {/* Come si integra nel workflow */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Zap className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Progettato per workflow di monitoraggio reali
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              L'analisi AI è più efficace quando combinata con ricerca e monitoraggio strutturati. Un workflow tipico:
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {[
                { icon: Search, title: "Cerca", desc: "Esegui una query strutturata sui dati TED" },
                { icon: Filter, title: "Filtra", desc: "Affina per CPV, paesi, parole chiave" },
                { icon: FileText, title: "Analizza", desc: "L'AI sintetizza ed estrae punti chiave" },
                { icon: TrendingUp, title: "Prioritizza", desc: "Concentrati prima sulle migliori opportunità" },
              ].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="p-4 bg-background rounded-lg border text-center">
                  <div className="mb-2 flex justify-center">
                    <Icon className="h-6 w-6 text-primary" />
                  </div>
                  <p className="font-medium text-foreground text-sm">{title}</p>
                  <p className="text-xs text-muted-foreground mt-1">{desc}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-4 text-sm">
              <Link to="/it/monitoraggio-bandi-ue" className="text-primary hover:underline font-medium">
                Esplora il monitoraggio bandi UE →
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-primary hover:underline font-medium">
                Configura gli alert sui bandi →
              </Link>
            </div>
          </div>
        </section>

        {/* Fiducia / Conformità */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Shield className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">
                Orientato alla conformità per design
              </h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              L'analisi AI opera all'interno di una piattaforma progettata per requisiti di utilizzo professionale, con
              accesso sicuro e gestione privacy-conscious dei dati account.
            </p>

            <div className="flex flex-wrap gap-4 mb-4">
              {[
                { icon: Shield, label: "Autenticazione sicura" },
                { icon: Globe, label: "Copertura UE" },
                { icon: Lock, label: "Accesso crittografato" },
              ].map(({ icon: Icon, label }) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/10 text-primary rounded-full text-xs font-medium"
                >
                  <Icon className="h-3.5 w-3.5" />
                  {label}
                </span>
              ))}
            </div>

            <p className="mt-4">
              <Link to="/it/conformita-sicurezza" className="text-primary hover:underline font-medium text-sm">
                Scopri conformità e sicurezza →
              </Link>
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Trasforma i risultati dei bandi in opportunità prioritizzate
            </h2>
            <p className="text-base mb-6 opacity-90">
              Inizia a usare l'analisi AI per ridurre i tempi di lettura e concentrarti sui bandi che contano.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/monitoraggio-bandi-ue" className="text-muted-foreground hover:text-foreground">
                Monitoraggio bandi UE
              </Link>
              <Link to="/it/alert-bandi-ted" className="text-muted-foreground hover:text-foreground">
                Alert bandi
              </Link>
              <Link to="/data-source" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
